import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sparkles, Link2, X, RefreshCcw } from "lucide-react";

/**
 * PlayHQLinksPanel
 * ================
 * Admin surface for the "approve now, link later" flow:
 *
 * 1. Suggestions list — every unlinked approved member whose name has
 *    just shown up in a finalised PlayHQ scorecard. Each row has:
 *      • Member name + member id
 *      • Suggested PlayHQ player name + confidence score
 *      • Actions: [ Link ] (one-click write of playhq_id) | [ Dismiss ]
 *
 * 2. Member managers section — every admin/primary_admin, with a
 *    switch toggling ``is_member_manager``. Only member managers get
 *    the in-app notification when a new suggestion appears.
 */
export default function PlayHQLinksPanel({ onDataChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // id -> bool
  // Post-link merge prompt state. When we detect a guest player who
  // shadows a just-linked member we surface this dialog with a stats
  // preview before absorbing the guest.
  const [mergePrompt, setMergePrompt] = useState(null); // { member: {id,name}, guest }
  const [mergeBusy, setMergeBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const sRes = await api.get("/playhq/link-suggestions");
      setSuggestions(sRes.data || []);
    } catch (e) {
      toast.error("Could not load link suggestions");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setRowBusy = (id, v) => setBusy((s) => ({ ...s, [id]: v }));

  const linkSuggestion = async (row) => {
    setRowBusy(row.id, true);
    try {
      const linkRes = await api.post(`/playhq/link-suggestions/${row.id}/link`);
      toast.success(`Linked ${row.user_first_name} ${row.user_last_name}`);
      // Check for a guest player that shadows this now-linked member.
      // If one exists, open the merge preview dialog so the admin can
      // eyeball the historical stats before absorbing.
      try {
        const gRes = await api.get("/playhq/admin/guest-merge-candidate", {
          params: { member_user_id: row.user_id, playhq_id: linkRes.data?.playhq_id || "" },
        });
        const guest = gRes.data?.guest;
        if (guest) {
          setMergePrompt({
            member: {
              id: row.user_id,
              first_name: row.user_first_name,
              last_name: row.user_last_name,
            },
            guest,
          });
        }
      } catch { /* silent — merge is optional */ }
      await load();
      onDataChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link — try the PlayHQ IDs tab manually.");
    } finally {
      setRowBusy(row.id, false);
    }
  };

  const confirmMerge = async () => {
    if (!mergePrompt) return;
    setMergeBusy(true);
    try {
      await api.post("/playhq/admin/guest-merge", {
        member_user_id: mergePrompt.member.id,
        guest_user_id: mergePrompt.guest.id,
      });
      toast.success("Guest history merged into member");
      setMergePrompt(null);
      await load();
      onDataChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setMergeBusy(false);
    }
  };

  const dismissSuggestion = async (row) => {
    setRowBusy(row.id, true);
    try {
      await api.post(`/playhq/link-suggestions/${row.id}/dismiss`);
      toast.success("Suggestion dismissed");
      await load();
      onDataChange?.();
    } catch (e) {
      toast.error("Could not dismiss suggestion");
    } finally {
      setRowBusy(row.id, false);
    }
  };

  const pending = useMemo(() => suggestions.filter((s) => s.status === "pending"), [suggestions]);

  return (
    <div className="mt-4 grid gap-5">
      {/* Suggestions */}
      <Card className="p-5 rounded-2xl" data-testid="playhq-links-suggestions">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[hsl(var(--accent))]" />
            <div className="font-semibold tracking-tight">PlayHQ link suggestions</div>
            {pending.length > 0 && (
              <Badge variant="outline" className="rounded-full border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))]">
                {pending.length} pending
              </Badge>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="playhq-links-refresh">
            <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading suggestions…</div>
        ) : pending.length === 0 ? (
          null
        ) : (
          <div className="space-y-2">
            {pending.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border hover:bg-[hsl(var(--secondary))]/40 transition-colors"
                data-testid={`playhq-link-suggestion-${row.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {row.user_first_name} {row.user_last_name}
                    {row.user_member_id && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground font-mono-sm">{row.user_member_id}</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate flex items-center gap-2">
                    Suggested match: <span className="font-medium text-foreground">{row.matched_player_name}</span>
                    <Badge variant="secondary" className="rounded-full text-[10px] px-1.5 py-0">
                      {Math.round((row.score || 0) * 100)}% match
                    </Badge>
                    {!row.suggested_playhq_id && (
                      <Badge variant="outline" className="rounded-full text-[10px] px-1.5 py-0 border-amber-400/50 text-amber-500">
                        No id yet
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    disabled={!row.suggested_playhq_id || !!busy[row.id]}
                    onClick={() => linkSuggestion(row)}
                    className="rounded-full h-8 px-3"
                    data-testid={`playhq-link-btn-${row.id}`}
                  >
                    <Link2 className="h-3.5 w-3.5 mr-1" /> Link
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!!busy[row.id]}
                    onClick={() => dismissSuggestion(row)}
                    className="rounded-full h-8 px-2 text-muted-foreground"
                    data-testid={`playhq-dismiss-btn-${row.id}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Guest merge preview — opens after a one-click link when a
          matching guest player already exists. Shows their aggregated
          match / runs / wickets / catches counts so the admin can
          eyeball before folding the guest into the member. */}
      <Dialog open={!!mergePrompt} onOpenChange={(o) => !o && !mergeBusy && setMergePrompt(null)}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="playhq-merge-preview">
          <DialogHeader>
            <DialogTitle>Merge guest history?</DialogTitle>
          </DialogHeader>
          {mergePrompt && (() => {
            const gs = mergePrompt.guest.stats || {};
            const hasStats = (gs.matches || 0) > 0;
            const memberName = `${mergePrompt.member.first_name || ""} ${mergePrompt.member.last_name || ""}`.trim();
            return (
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  A guest record for{" "}
                  <span className="text-foreground font-medium">
                    {mergePrompt.guest.first_name} {mergePrompt.guest.last_name}
                  </span>{" "}
                  already exists. Merging will fold their history into{" "}
                  <span className="text-foreground font-medium">{memberName}</span> and delete the guest record.
                </div>
                {hasStats ? (
                  <div className="grid grid-cols-4 gap-2" data-testid="playhq-merge-preview-stats">
                    {[
                      {label: "Matches", value: gs.matches},
                      {label: "Runs", value: gs.runs},
                      {label: "Wickets", value: gs.wickets},
                      {label: "Catches", value: gs.catches},
                    ].map((s) => (
                      <div key={s.label} className="rounded-lg border border-border p-2 text-center">
                        <div className="text-xs text-muted-foreground">{s.label}</div>
                        <div className="text-lg font-semibold tabular-nums">{s.value ?? 0}</div>
                      </div>
                    ))}
                    {(gs.seasons || 0) > 0 && (
                      <div className="col-span-4 text-[11px] text-muted-foreground text-center pt-1">
                        {gs.seasons} season{gs.seasons === 1 ? "" : "s"}
                        {gs.latest_season ? ` · latest ${gs.latest_season}` : ""}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground text-center">
                    No historical stats recorded against this guest yet — merging will simply remove the duplicate record.
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergePrompt(null)} disabled={mergeBusy} data-testid="playhq-merge-preview-cancel">
              Not now
            </Button>
            <Button
              onClick={confirmMerge}
              disabled={mergeBusy}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="playhq-merge-preview-confirm"
            >
              {mergeBusy ? "Merging…" : "Merge history"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
